/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-empty-function */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Search, X, Trash2, Users, Loader2, Mail, User } from 'lucide-react';
import { SharedUser } from '../../utils/file-manager';
import { useDocumentSharedUsersQuery } from '../../hooks/use-document-manager';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'components/core/theme-provider';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { useGroupsAndUsers } from 'features/iam/hooks/use-iam';

interface PermissionOption {
  value: string;
  label: string;
}

interface ShareWithMeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    usersToShare: SharedUser[],
    usersToUnshare: SharedUser[],
    selectedGroups: Group[],
    groupsUnshare: Group[],
    isGroupShare: boolean,
    permissions: { [key: string]: string }) => Promise<void>;
  currentSharedUsers?: SharedUser[];
  currentSharedGroups?: Group[];
  availableRoles?: PermissionOption[];
  defaultRole?: string;
}

interface Group {
  id: string;
  groupName: string;
  description?: string;
  memberCount?: number;
}

const Avatar: React.FC<{ name: string; size?: 'sm' | 'md' }> = ({ name, size = 'md' }) => {
  const initials = name?.trim()[0]?.toUpperCase();
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';

  return (
    <div
      className={`${sizeClasses} rounded-full bg-primary text-white flex items-center justify-center font-medium`}
    >
      {initials}
    </div>
  );
};

const GroupAvatar: React.FC<{ name: string; size?: 'sm' | 'md' }> = ({ name, size = 'md' }) => {
  const initials = name?.trim()?.split(' ')?.map(word => word[0])?.join('')?.toUpperCase()?.slice(0, 1);
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 text-sm';

  return (
    <div
      className={`${sizeClasses} rounded-full bg-primary text-white flex items-center justify-center font-medium`}
    >
      {initials}
    </div>
  );
};

export const ShareWithMeModal: React.FC<ShareWithMeModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  currentSharedUsers = [],
  currentSharedGroups = [],
  defaultRole = 'Editor',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMode, setSearchMode] = useState<'email' | 'name'>('name');
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<SharedUser[]>(currentSharedUsers);
  const [permissions, setPermissions] = useState<{ [key: string]: string }>(() => {
    const initialPermissions: { [key: string]: string } = {};
    currentSharedUsers.forEach((user) => {
      initialPermissions[user.id] = user.role ?? defaultRole;
    });
    return initialPermissions;
  });
  const { t } = useTranslation();
  const { theme } = useTheme();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'people' | 'group'>('people');
  const [currentView, setCurrentView] = useState<'share' | 'manage'>('share');
  const [groupView, setGroupView] = useState<'share' | 'manage'>('share');
  const [defaultPermission, setDefaultPermission] = useState<UserRole>(defaultRole as UserRole);
  const { mutateAsync: getGroupsAndUsers } = useGroupsAndUsers();

  // Group-related states
  const [selectedGroups, setSelectedGroups] = useState<Group[]>(currentSharedGroups);
  const [allGroups, setAllGroups] = useState<Group[]>([]);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [hasMoreGroups, setHasMoreGroups] = useState(true);
  const [page, setPage] = useState(0);
  const pageSize = 10;

  type UserRole = 'Editor' | 'Viewer' | 'Owner';

  const debouncedChange = useMemo(
    () =>
      debounce((val: string) => {
        setSearchQuery(val);
      }, 500),
    [setSearchQuery]
  );

  // Setup scroll listener for infinite scroll - GROUP TAB ONLY
  useEffect(() => {
    if (activeTab !== 'group') return;

    const container = document.getElementById("group-scroll-container");
    if (!container) return;

    const onScroll = () => {
      const isBottom =
        container.scrollTop + container.clientHeight >= container.scrollHeight - 20;

      if (isBottom && !isLoadingGroups && hasMoreGroups) {
        setPage(prev => prev + 1);
      }
    };

    container.addEventListener("scroll", onScroll);

    return () => container.removeEventListener("scroll", onScroll);
  }, [activeTab, isLoadingGroups, hasMoreGroups]);


  useEffect(() => {
    if (currentSharedGroups) {
      setSelectedGroups((prev) => {
        const newSelectedGroups = [...prev];
        currentSharedGroups.forEach((group) => {
          const existingGroup = newSelectedGroups.find((g) => g.id === group.id);
          if (!existingGroup) {
            newSelectedGroups.push(group);
          }
        });
        return newSelectedGroups;
      });
    }
  }, [isOpen, currentSharedGroups]);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedChange(e.target.value);
    setInputValue(e.target.value);
  };

  const queryFilter = useMemo(() => {
    if (!searchQuery) return null;
    if (searchMode === 'email') {
      return { email: searchQuery };
    }
    if (searchMode === 'name') {
      return { name: searchQuery };
    }
  }, [searchQuery, searchMode]);

  const { data: sharedUsers = [] } = useDocumentSharedUsersQuery({
    page: 0,
    pageSize: 50,
    filter: queryFilter || {},
    enabled: isOpen && !!queryFilter,
  });

  const toggleSearchMode = () => {
    setSearchMode((prev) => (prev === 'email' ? 'name' : 'email'));
    setInputValue('');
    setSearchQuery('');
  };

  const getGroupsData = async (currentPage: number, isNewSearch = false) => {
    if (!isOpen) return;

    setIsLoadingGroups(true);
    try {
      const response: any = await getGroupsAndUsers({
        page: currentPage,
        pageSize: pageSize,
        userPage: '0',
        UserPageSize: '10'
      });

      if (response && response.groupsDetails) {
        if (isNewSearch || currentPage === 0) {
          // Reset groups for new search or first load
          setAllGroups(response.groupsDetails);
        } else {
          // Append groups for infinite scroll
          setAllGroups(prev => [...prev, ...response.groupsDetails]);
        }

        // Check if there are more groups to load
        setHasMoreGroups(response.groupsDetails.length === pageSize);
      } else {
        setAllGroups([]);
        setHasMoreGroups(false);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      if (isNewSearch || currentPage === 0) {
        setAllGroups([]);
      }
      setHasMoreGroups(false);
    } finally {
      setIsLoadingGroups(false);
    }
  };

  // Reset pagination and fetch groups when tab changes to group or modal opens
  useEffect(() => {
    if (isOpen && activeTab === 'group') {
      setPage(0);
      setHasMoreGroups(true);
      getGroupsData(0, true);
    }
  }, [isOpen, activeTab]);

  // Load more groups when page changes - GROUP TAB ONLY
  useEffect(() => {
    if (isOpen && activeTab === 'group' && page > 0) {
      getGroupsData(page, false);
    }
  }, [page]);

  useEffect(() => {
    const initialPermissions: { [key: string]: string } = {};
    const syncedUsers = currentSharedUsers.map((user) => {
      const userPermission = user.role ?? (defaultRole as UserRole);
      initialPermissions[user.id] = userPermission;
      return { ...user, role: userPermission };
    });

    setSelectedUsers(syncedUsers);
    setPermissions(initialPermissions);
  }, [currentSharedUsers, defaultRole]);

  const handleCancel = useCallback(() => {
    setSelectedUsers(currentSharedUsers);
    setPermissions(() => {
      const initialPermissions: { [key: string]: string } = {};
      currentSharedUsers.forEach((user) => {
        initialPermissions[user.id] = user.role ?? defaultRole;
      });
      return initialPermissions;
    });
    setSearchQuery('');
    setInputValue('');
    setSelectedGroups([]);
    setCurrentView('share');
    setGroupView('share');
    setDefaultPermission(defaultRole as UserRole);
    setActiveTab('people');
    setPage(0);
    setHasMoreGroups(true);
    onClose();
  }, [currentSharedUsers, defaultRole, onClose]);

  const handleConfirm = useCallback(async () => {

    const newlySharedUsers = selectedUsers.filter(
      (u) => !currentSharedUsers.some((c) => c.id === u.id)
    );
    const newlyUnsharedUsers = currentSharedUsers.filter(
      (u) => !selectedUsers.some((s) => s.id === u.id)
    );

    const newlySharedGroups = selectedGroups.filter(
      (u) => !currentSharedGroups.some((c) => c.id === u.id)
    );

    const newlyUnshareGroups = currentSharedGroups.filter(
      (u) => !selectedGroups.some((s) => s.id === u.id)
    );

    setLoading(true);
    await onConfirm(
      newlySharedUsers,
      newlyUnsharedUsers,
      newlySharedGroups,
      newlyUnshareGroups,
      activeTab === 'group' ? true : false,
      permissions
    );
    setLoading(false);
    onClose();
    setCurrentView('share');
    setGroupView('share');
    setDefaultPermission(defaultRole as UserRole);
    setInputValue('');
    setSearchQuery('');
    setSelectedGroups([]);
    setPage(0);
    setHasMoreGroups(true);
    queryClient.invalidateQueries({
      queryKey: ['document-files'],
    });
    setSelectedUsers([]);
  }, [selectedUsers, permissions, onConfirm, onClose, queryClient, currentSharedUsers, defaultRole, selectedGroups, activeTab, currentSharedGroups]);

  const handleUserToggle = useCallback(
    (user: SharedUser) => {
      setSelectedUsers((prev) => {
        const isSelected = prev.some((u) => u.id === user.id);
        if (isSelected) {
          setPermissions((prevPermissions) => {
            const newPermissions = { ...prevPermissions };
            delete newPermissions[user.id];
            return newPermissions;
          });
          return prev.filter((u) => u.id !== user.id);
        } else {
          setPermissions((prevPermissions) => ({
            ...prevPermissions,
            [user.id]: defaultPermission,
          }));
          return [...prev, { ...user, role: defaultPermission }];
        }
      });
    },
    [defaultPermission]
  );

  const handleRemoveUser = useCallback((userId: string) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
    setPermissions((prev) => {
      const newPermissions = { ...prev };
      delete newPermissions[userId];
      return newPermissions;
    });
  }, []);

  const handleGroupToggle = useCallback((group: Group) => {
    setSelectedGroups(prev => {
      const isSelected = prev.some(g => g.id === group.id);
      if (isSelected) {
        return prev.filter(g => g.id !== group.id);
      } else {
        return [...prev, group];
      }
    });
  }, []);

  const handleRemoveGroup = useCallback((groupId: string) => {
    setSelectedGroups(prev => prev.filter(g => g.id !== groupId));
  }, []);

  if (!isOpen) return null;

  const getDialogTitle = () => {
    if (activeTab === 'people') {
      if (currentView === 'share') {
        return t('SHARE');
      }
      if (selectedUsers.length > 0) {
        return t('LISTED_PEOPLE');
      }
      return t('SHARE_PEOPLE');
    } else {
      // Group tab
      if (groupView === 'share') {
        return t('SHARE');
      }
      if (selectedGroups.length > 0) {
        return t('LISTED_GROUPS') || 'Listed Groups';
      }
      return t('SHARE_GROUPS') || 'Share with Groups';
    }
  };

  return (
    <div style={{ marginTop: 0 }} className="fixed inset-0 flex items-center justify-center z-50">
      <div className="bg-card w-full max-w-md mx-4 max-h-[80vh] flex flex-col shadow-xl rounded-[10px]">
        <div className="p-4 border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className={theme === 'dark' ? 'text-lg font-medium text-gray' : 'text-lg font-medium text-gray-900'}>{getDialogTitle()}</h2>
            <button
              onClick={handleCancel}
              className="w-5 h-5 relative overflow-hidden flex items-center justify-center group transition-opacity hover:opacity-80 ml-3 border-2 bg-white border-[#959595] rounded-full"
            >
              <X className="w-[12px] h-[12px] text-[#959595] hover:text-[#959595]" strokeWidth={4} />
              <span className="sr-only">Close</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 rounded-md p-1 border border-primary p-1 rounded-lg font-medium">
            <button
              onClick={() => setActiveTab('people')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'people'
                ? ` bg-primary-600 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`
                : `${theme === 'dark' ? 'text-primary-300' : 'text-neutral-800'}`
                }`}
            >
              {t('PEOPLE') || 'People'}
            </button>
            <button
              onClick={() => setActiveTab('group')}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'group'
                ? `bg-primary-600 ${theme === 'dark' ? ' text-neutral-800' : 'text-neutral-50'}`
                : `${theme === 'dark' ? 'text-primary-300' : 'text-neutral-800'}`
                }`}
            >
              {t('GROUP') || 'Group'}
            </button>
          </div>
        </div>

        {/* People Tab Content */}
        {activeTab === 'people' && (
          <>
            {currentView === 'share' && (
              <>
                <div className="p-4">
                  <h3 className={theme === 'dark' ? 'text-sm font-medium text-gray mb-3' : 'text-sm font-medium text-gray-900 mb-3'}>{t('SHARE_WITH_PEOPLE')}</h3>

                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-primary" />
                    <input
                      type="text"
                      placeholder={`${t('SEARCH_BY')} ${searchMode === 'email' ? t('EMAIL').toLowerCase() : t('NAME').toLowerCase()}`}
                      value={inputValue}
                      onChange={handleInputChange}
                      className="
                                        h-10 w-full rounded-3xl
                                        border border-gray-300
                                        bg-background pl-9 pr-8
                                        focus:outline-none
                                        focus:border-primary
                                        focus:ring-1 focus:ring-primary
                                    "
                    />
                    <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleSearchMode}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 text-primary"
                      >
                        {searchMode === 'email' ? <Mail className="h-4 w-4" /> : <User className="h-4 w-4" />}
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 pb-4">
                  {searchQuery ? (
                    <div className="space-y-2">
                      {sharedUsers.map((user) => {
                        const isSelected = selectedUsers.some((u) => u.id === user.id);
                        return (
                          <button
                            key={user.id}
                            onClick={() => handleUserToggle(user)}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer text-red ${theme === 'dark' ? 'hover:bg-muted' : 'hover:bg-gray-200'} w-full text-left ${isSelected ? ' border border-black-500' : ''
                              }`}
                            aria-pressed={isSelected}
                            aria-label={`${isSelected ? 'Deselect' : 'Select'} user ${user.name}`}
                          >
                            <Avatar name={user.name} size='sm' />
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium truncate ${theme === 'dark' ? 'text-primary' : 'text-gray-900'}`}>
                                {user.name}
                              </div>
                              <div className="text-xs text-gray-500 truncate">{user.email}</div>
                            </div>
                            {isSelected && <Users className="w-4 h-4 text-primary" />}
                          </button>
                        );
                      })}
                      {sharedUsers.length === 0 && (
                        <div className="text-center text-gray-500 text-sm py-8">{t("NO_USERS_FOUND")}</div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center text-gray-500 text-sm py-12">
                      {t("THE_LIST_IS_CURRENTLY_EMPTY")}
                    </div>
                  )}
                </div>
              </>
            )}

            {currentView === 'manage' && (
              <>
                <div className="p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-1">{t("SHARE_WITH_PEOPLE")}</h3>
                  <div className="text-xs text-gray-500">{selectedUsers.length} {t("ADDED_TO_THE_LIST")}</div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 pb-4">
                  <div className="space-y-3">
                    {selectedUsers.map((user) => (
                      <div key={user.id} className="flex items-center gap-3 p-2">
                        <Avatar name={user?.name || user.id} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-500 truncate">{user.name}</div>
                          <div className="text-xs text-gray-500 truncate">{user.email}</div>
                        </div>
                        <button
                          onClick={() => handleRemoveUser(user.id)}
                          className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Group Tab Content - WITH MANAGE FUNCTIONALITY */}
        {activeTab === 'group' && (
          <>
            {groupView === 'share' && (
              <>
                <div className="flex-1 px-4 pb-4">
                  <div className="border rounded-md">
                    <div className="space-y-2 p-2 max-h-[300px] overflow-y-auto" id="group-scroll-container">
                      {allGroups.map((group) => {
                        const isSelected = selectedGroups.some(g => g.id === group.id);
                        return (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => handleGroupToggle(group)}
                            className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer ${theme === 'dark' ? 'hover:bg-muted' : 'hover:bg-gray-200'} w-full text-left ${isSelected ? ' border border-black-500' : ''
                              }`}
                          >
                            <GroupAvatar name={group.groupName} size='sm' />
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-medium ${theme === 'dark' ? 'text-primary' : 'text-gray-900'
                                } truncate`}>
                                {group.groupName}
                              </div>
                              {group.description && (
                                <div className="text-xs text-gray-500 truncate">{group.description}</div>
                              )}
                              {group.memberCount !== undefined && (
                                <div className="text-xs text-gray-400">{group.memberCount} {t('MEMBERS') || 'members'}</div>
                              )}
                            </div>
                          </button>
                        );
                      })}

                      {isLoadingGroups && (
                        <div className="flex justify-center py-4">
                          <Loader2 className="w-5 h-5 animate-spin text-primary" />
                        </div>
                      )}

                      {!isLoadingGroups && allGroups.length === 0 && (
                        <div className="text-center py-4 text-sm text-gray-500">
                          {t('NO_GROUPS_FOUND') || 'No groups found'}
                        </div>
                      )}

                      {!hasMoreGroups && allGroups.length > 0 && (
                        <div className="text-center py-4 text-sm text-gray-400">
                          {t('NO_MORE_GROUPS') || 'No more groups to load'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}

            {groupView === 'manage' && (
              <>
                <div className="p-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-1">{t("SHARE_WITH_GROUPS") || "Share with Groups"}</h3>
                  <div className="text-xs text-gray-500">{selectedGroups.length} {t("ADDED_TO_THE_LIST") || "added to the list"}</div>
                </div>

                <div className="flex-1 overflow-y-auto px-4 pb-4">
                  <div className="space-y-3">
                    {selectedGroups.map((group) => (
                      <div key={group.id} className="flex items-center gap-3 p-2">
                        <GroupAvatar name={group.groupName} size="sm" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-500 truncate">{group.groupName}</div>
                          {group.description && (
                            <div className="text-xs text-gray-500 truncate">{group.description}</div>
                          )}
                          {group.memberCount !== undefined && (
                            <div className="text-xs text-gray-400">{group.memberCount} {t('MEMBERS') || 'members'}</div>
                          )}
                        </div>
                        <button
                          onClick={() => handleRemoveGroup(group.id)}
                          className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </>
        )}

        {/* Footer */}
        <div className={`${theme === 'dark' ? '' : 'bg-gray-50'} p-4 border-gray-200 flex items-center justify-between`}>
          {/* <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-200 rounded-md transition-colors"
          >
            {t("CANCEL")}
          </button> */}

          <Button variant="outline" onClick={handleCancel}>
            {t('CANCEL')}
          </Button>

          <div className="flex items-center gap-2">
            {activeTab === 'people' && (
              <>
                {currentView === 'share' && selectedUsers.length > 0 && (
                  <button
                    onClick={() => setCurrentView('manage')}
                    className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  >
                    {t("MANAGE")} ({selectedUsers.length})
                  </button>
                )}
                {currentView === 'manage' && (
                  <Button
                    variant="outline"
                    onClick={() => setCurrentView('share')}
                    className={`px-4 py-2 text-sm ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'} hover:bg-accent rounded-md transition-colors rounded-lg`}
                  >
                    {t("BACK")}
                  </Button>
                )}
                <Button
                  onClick={handleConfirm}
                  className={`bg-primary-600 hover:bg-primary-700 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`}
                >
                  {loading ? <Loader2 className="animate-spin" /> : `${t("SAVE")}`}
                </Button>
              </>
            )}

            {activeTab === 'group' && (
              <>
                {groupView === 'share' && selectedGroups.length > 0 && (
                  <button
                    onClick={() => setGroupView('manage')}
                    className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                  >
                    {t("MANAGE")} ({selectedGroups.length})
                  </button>
                )}
                {groupView === 'manage' && (
                  <Button
                    variant="outline"
                    onClick={() => setGroupView('share')}
                    className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-md transition-colors rounded-lg"
                  >
                    {t("BACK")}
                  </Button>
                )}

                <Button
                  onClick={handleConfirm}
                  disabled={selectedGroups.length === 0}
                  className={`bg-primary-600 hover:bg-primary-700 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`}
                >
                  {loading ? <Loader2 className="animate-spin" /> : `${t("SAVE")}`}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};